import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { SalesPersonToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { DateInput } from '~/components/date-input';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useSalesPersonMutations } from '~/hooks/use-sales-persons';
import { salesPersonSchema, EMPTY_SALES_PERSON, tokenToForm, type SalesPersonFormValues } from '~/lib/schemas/sales-person';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	row: SalesPersonToken | null;
	/** Other sales persons (for the replacement-person FK); the current row is excluded. */
	all: SalesPersonToken[];
}

/** Add / edit a sales person (Πωλητής). */
export function SalesPersonEditDialog({ open, onOpenChange, row, all }: Props) {
	const { t } = useTranslation();
	const { create, update } = useSalesPersonMutations();

	const form = useForm<SalesPersonFormValues>({
		resolver: zodResolver(salesPersonSchema) as Resolver<SalesPersonFormValues>,
		defaultValues: row ? tokenToForm(row) : EMPTY_SALES_PERSON,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, control, formState } = form;
	const isEdit = Boolean(row?.id);

	useEffect(() => {
		if (open) reset(row ? tokenToForm(row) : EMPTY_SALES_PERSON);
	}, [open, row, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync({ form: values, original: row });
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const fld = (key: string) => t(`customers.salesPersons.fields.${key}`);
	const replacements = all.filter(p => p.id !== row?.id);
	const personName = (p: SalesPersonToken) => [p.fn, p.ln].filter(Boolean).join(' ').trim() || p.usr || `#${p.id}`;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{isEdit ? t('customers.salesPersons.editTitle') : t('customers.salesPersons.addTitle')}</DialogTitle>
					<DialogDescription>{t('customers.salesPersons.formSubtitle')}</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<FormField label={fld('username')} error={formState.errors.usr?.message}>
							<Input {...register('usr')} maxLength={255} aria-invalid={Boolean(formState.errors.usr)} autoFocus />
						</FormField>
						<FormField label={isEdit ? fld('passwordEdit') : fld('password')} error={formState.errors.psw?.message}>
							<Input type="password" autoComplete="new-password" {...register('psw')} aria-invalid={Boolean(formState.errors.psw)} />
						</FormField>
						<FormField label={fld('name')} error={formState.errors.fn?.message}>
							<Input {...register('fn')} maxLength={50} />
						</FormField>
						<FormField label={fld('surname')} error={formState.errors.ln?.message}>
							<Input {...register('ln')} maxLength={50} />
						</FormField>
						<FormField label={fld('email')} error={formState.errors.eml?.message}>
							<Input {...register('eml')} maxLength={255} />
						</FormField>
						<FormField label={fld('erpCode')} error={formState.errors.erpcd?.message}>
							<Input {...register('erpcd')} maxLength={50} />
						</FormField>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="sp-act">{fld('active')}</Label>
							<Switch id="sp-act" checked={watch('act')} onCheckedChange={next => setValue('act', next, { shouldDirty: true })} />
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="sp-cfm">{fld('confirmed')}</Label>
							<Switch id="sp-cfm" checked={watch('cfm')} onCheckedChange={next => setValue('cfm', next, { shouldDirty: true })} />
						</div>
					</div>

					<div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
						<FormField label={fld('replaceSalesPerson')}>
							<Controller
								control={control}
								name="rspid"
								render={({ field }) => (
									<Select
										value={field.value != null ? String(field.value) : 'none'}
										onValueChange={v => field.onChange(v === 'none' ? null : Number(v))}>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="none">—</SelectItem>
											{replacements.map(p => (
												<SelectItem key={p.id} value={String(p.id)}>
													{personName(p)}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</FormField>
						<FormField label={fld('replaceStart')}>
							<Controller
								control={control}
								name="rsd"
								render={({ field }) => (
									<DateInput
										value={field.value ? new Date(field.value) : null}
										onChange={d => field.onChange(d ? d.toISOString() : null)}
									/>
								)}
							/>
						</FormField>
						<FormField label={fld('replaceEnd')}>
							<Controller
								control={control}
								name="red"
								render={({ field }) => (
									<DateInput
										value={field.value ? new Date(field.value) : null}
										onChange={d => field.onChange(d ? d.toISOString() : null)}
									/>
								)}
							/>
						</FormField>
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
